import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import API, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CandidateLayout from "./CandidateLayout";
import EmployerLayout from "./EmployerLayout";
import { useAppT } from "../components/home/i18n";
import { ShieldCheck, Download, Trash2, XCircle } from "lucide-react";

export default function AccountPrivacy() {
  const { user } = useAuth();
  const { a } = useAppT();
  const Layout = user?.role === "employer" ? EmployerLayout : CandidateLayout;
  const [consent, setConsent] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    const [c, r] = await Promise.all([API.get("/account/consent"), API.get("/account/requests")]);
    setConsent(c.data); setRequests(r.data.requests);
  };
  useEffect(() => { load(); }, []);

  const request = async (type) => {
    try {
      await API.post("/account/requests", { type });
      toast.success(a.account.requestSubmitted);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openOf = (t) => requests.find((r) => r.type === t && r.status === "open");

  const CARDS = [
    { type: "data_export", icon: Download },
    { type: "consent_withdrawal", icon: XCircle },
    { type: "account_deletion", icon: Trash2 },
  ];

  return (
    <Layout>
      <h1 className="font-serif text-3xl text-[#173A2C]">{a.account.title}</h1>
      <p className="mt-1 text-sm text-[#5C5855]">{a.account.sub}</p>

      <div className="mt-5 rounded-md border border-[#E8E4DB] bg-white p-4" data-testid="consent-status">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#173A2C]"><ShieldCheck className="h-4 w-4 text-[#2E6B4B]" /> {a.account.yourConsent}</div>
        {consent?.consent ? (
          <div className="mt-1 text-xs text-[#5C5855]">
            {a.account.acceptedOn} {consent.consent.accepted_at ? new Date(consent.consent.accepted_at).toLocaleString() : "—"} · {a.account.versionsWord}: {a.account.terms} {consent.consent.versions?.terms}, {a.account.privacy} {consent.consent.versions?.privacy}, {a.account.guidelines} {consent.consent.versions?.guidelines}
          </div>
        ) : <div className="mt-1 text-xs text-[#8a8580]">{a.account.noConsent}</div>}
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          <Link to="/terms" target="_blank" className="text-[#1F4A38] underline">{a.account.terms}</Link>
          <Link to="/privacy" target="_blank" className="text-[#1F4A38] underline">{a.account.privacy}</Link>
          <Link to="/guidelines" target="_blank" className="text-[#1F4A38] underline">{a.account.guidelines}</Link>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {CARDS.map(({ type, icon: Icon }) => {
          const open = openOf(type);
          const card = a.account.cards[type];
          return (
            <div key={type} className="rounded-md border border-[#E8E4DB] bg-white p-4">
              <Icon className="h-5 w-5 text-[#1F4A38]" />
              <div className="mt-2 text-sm font-semibold text-[#2C2A29]">{card.title}</div>
              <div className="mt-1 text-xs text-[#5C5855]">{card.desc}</div>
              <button data-testid={`request-${type}`} disabled={!!open} onClick={() => request(type)}
                className="mt-3 rounded-full border border-[#1F4A38] px-4 py-1.5 text-xs font-semibold text-[#1F4A38] disabled:opacity-50">
                {open ? a.common.requested : a.common.request}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-6" data-testid="my-requests">
        <h2 className="text-sm font-semibold text-[#173A2C]">{a.account.yourRequests}</h2>
        {requests.length === 0 ? <div className="mt-1 text-xs text-[#8a8580]">{a.account.noRequests}</div> : (
          <div className="mt-2 space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-sm border border-[#E8E4DB] bg-white px-3 py-2 text-xs">
                <span>{a.account.labels[r.type] || r.type} · <span className="text-[#5C5855]">{new Date(r.created_at).toLocaleDateString()}</span></span>
                <span className={`rounded-full px-2 py-0.5 font-semibold ${r.status === "open" ? "bg-[#C4A46B]/20 text-[#8a6f38]" : "bg-[#2E6B4B]/12 text-[#2E6B4B]"}`}>{r.status === "open" ? a.adminReqs.filters.open : a.adminReqs.filters.resolved}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
